/**
 * Hotjar identity tagging.
 *
 * The base Hotjar snippet is loaded in `public/index.html` (hjid 5245947 prod /
 * 5187745 non-prod), behind a region gate: visitors in the EEA, the UK or
 * Switzerland never load it, and `window.__ttfTrackingBlocked` records that
 * decision. Everything below is a no-op for them. The gate lives in the HTML
 * because it has to run before Hotjar does — see that file's comment.
 *
 * This module tags the *active* recording with two anonymous
 * identifiers so a shopper's journey can be followed from the store widget into
 * the PWA and across PWA sessions:
 *
 *   - `ttf:pwa_user:<uid>`   the Firebase anon uid (same id `logger.ts` writes to
 *                            pwa_logs and scans are keyed on → joins Hotjar to
 *                            our backend data).
 *   - `ttf:session:<id>`     the `widget_sessions/{id}` id carried in via the
 *                            store deeplink → the join key back to the widget's
 *                            (separate) Hotjar site. Present only for
 *                            widget-originated sessions.
 *   - `ttf:from_widget`      low-cardinality marker: this recording came from a
 *                            widget deeplink (vs a walk-in).
 *
 * We use `tagRecording` (not the paid `identify`/User Attributes feature) to
 * match the store widget and work on any Hotjar plan. See ~/.claude/TRAPS.md.
 *
 * The uid and the sessionId become known at different (either-order) times, so
 * two triggers call `tagHotjarIdentity()`:
 *   1. auth resolves          → src/context/auth.context.tsx (onAuthStateChanged)
 *   2. a sessionId is written  → src/Utils/avatarSharingStorage.ts dispatches a
 *      `ttf:sharing-updated` DOM event that `initHotjarIdentity()` listens for.
 * `tagHotjarIdentity()` is idempotent, so firing from both — repeatedly — is safe.
 *
 * The event indirection (rather than avatarSharingStorage importing this module)
 * deliberately avoids a circular import: this module imports getAvatarSharingData.
 */

import { auth } from "@/Utils/firebase";
import {
  getAvatarSharingData,
  SHARING_UPDATED_EVENT,
} from "@/Utils/avatarSharingStorage";

declare global {
  interface Window {
    hj?: (...args: any[]) => void;
    /**
     * Set by the region gate in `public/index.html` BEFORE Hotjar is loaded.
     * `true` means the snippet was never injected for this visitor (EEA / UK /
     * Switzerland, an unreadable timezone, or `REACT_APP_HOTJAR_ENABLED=false`).
     * Read it — never re-derive the region here. The gate has to run in `<head>`
     * because React mounts long after Hotjar would have started recording.
     */
    __ttfTrackingBlocked?: boolean;
  }
}

// Each distinct value is tagged at most once per page load, so repeated triggers
// don't spam tagRecording with duplicates.
const tagged = new Set<string>();

/**
 * Tag the active Hotjar recording with whatever identity we currently know.
 * No-ops safely if Hotjar is absent (ad-blocked / not yet loaded — the inline
 * snippet's queue stub replays queued calls once the async script loads).
 * Never throws: analytics must not break auth or routing.
 */
export function tagHotjarIdentity(): void {
  try {
    if (typeof window === "undefined") return;

    // Redundant with the `window.hj` check below — when the gate blocks, the
    // snippet never defines `hj`, so tagging already no-ops. It is here anyway
    // so the no-op is intentional and greppable: without it, a future change
    // that revives `window.hj` (a direct import, a tag manager) would silently
    // resume tagging blocked visitors. See public/index.html.
    if (window.__ttfTrackingBlocked) return;

    if (typeof window.hj !== "function") return;

    const uid = auth.currentUser?.uid ?? null;
    const { sessionId } = getAvatarSharingData();

    const tags: string[] = [];

    if (uid && !tagged.has("u:" + uid)) {
      tags.push(`ttf:pwa_user:${uid}`);
      tagged.add("u:" + uid);
    }

    if (sessionId) {
      if (!tagged.has("s:" + sessionId)) {
        tags.push(`ttf:session:${sessionId}`);
        tagged.add("s:" + sessionId);
      }
      if (!tagged.has("from_widget")) {
        tags.push("ttf:from_widget");
        tagged.add("from_widget");
      }
    }

    if (tags.length) window.hj("tagRecording", tags);
  } catch {
    /* swallow — analytics must never break the app */
  }
}

/**
 * Wire the sessionId-change trigger and do an initial best-effort tag (covers
 * the case where uid and/or sessionId are already present at load, e.g. a
 * returning persisted anon user). Call once at app bootstrap.
 */
export function initHotjarIdentity(): void {
  if (typeof window === "undefined") return;
  // Blocked visitors get no listener at all, so no sharing-storage write can
  // reach Hotjar even if `window.hj` were to appear later in the page's life.
  if (window.__ttfTrackingBlocked) return;
  window.addEventListener(SHARING_UPDATED_EVENT, tagHotjarIdentity);
  tagHotjarIdentity();
}
